// src/components/sidebar/notes/notes.tsx (USER APP)
/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Header } from '../../header/header';
import { useChat } from '../../chat.hooks';
import { UniversalSpinner } from '../../../../components/universal-spinner/universal-spinner';

import {
  NotesSheet,
  NotesBody,
  NotesMain,
  NotesInner,
  MetaRow,
  NoteDateText,
  DotsButton,
  DotsInner,
  Dot,
  NoteContent,
  NoteEditor,
  NoteErrorBanner,
} from './notes.styled';

import { useNotes } from './notes.hooks';
import { useNotesCopyDeleteModal } from './modals/copy-delete/copy-delete.hooks';
import { NotesCopyDeleteModal } from './modals/copy-delete/copy-delete';
import { useConfirmDeleteModal } from './modals/copy-delete/confirm-delete/confirm-delete.hooks';
import { ConfirmDeleteModal } from './modals/copy-delete/confirm-delete/confirm-delete';
import { linkifyHtml } from '../../../../utils/linkify';

type NotesProps = {
  open: boolean;
  onClose: () => void;
  noteId: string | null;
  onSaved?: () => void;
  onDeleted?: () => void;
};

const Notes: React.FC<NotesProps> = ({ open, onClose, noteId, onSaved, onDeleted }) => {
  const { handleNotificationClick, handleProfileClick } = useChat();

  const editorRef = useRef<HTMLDivElement | null>(null);
  const dotsButtonRef = useRef<HTMLButtonElement | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<DOMRect | null>(null);

  // Prevent caret jump by not syncing DOM while focused
  const [isEditing, setIsEditing] = useState(false);

  // Reset isEditing on close — removing a focused contentEditable from the DOM
  // does NOT fire blur, so isEditing can stay true across close/reopen cycles.
  useEffect(() => {
    if (!open) setIsEditing(false);
  }, [open]);

  const {
    title,
    body,
    formattedCreatedAt,
    formattedUpdatedAt,
    isLoading,
    loadError,
    saveError,
    hasPersisted,
    copyToClipboard,
    deleteNote,
    setTitle,
    setBody,
  } = useNotes({
    noteId,
    open,
    onSaved,
    onDeleted,
  });

  const confirmDeleteModal = useConfirmDeleteModal({
    onConfirm: async () => {
      await deleteNote();
      onClose();
    },
  });

  const copyDeleteModal = useNotesCopyDeleteModal({
    onCopy: copyToClipboard,
    onDelete: () => {
      confirmDeleteModal.openModal();
    },
  });

  // Preserve exact spacing/newlines between title & body
  const editorText = useMemo(() => {
    return title + (body !== '' ? '\n' + body : '');
  }, [title, body]);

  // Keep latest editorText in a ref so the callback ref reads it without deps.
  const editorTextRef = useRef(editorText);
  editorTextRef.current = editorText;

  // Callback ref: sync content the moment the DOM element mounts (fires
  // synchronously during React commit — before any useEffect).
  const setEditorRef = useCallback((el: HTMLDivElement | null) => {
    editorRef.current = el;
    if (el && el.innerText !== editorTextRef.current) {
      el.innerHTML = linkifyHtml(editorTextRef.current);
    }
  }, []);

  // Also sync when editorText changes while already mounted (e.g. first load).
  // When not editing, render linkified HTML so URLs are clickable.
  useEffect(() => {
    if (!open || isEditing) return;
    const el = editorRef.current;
    if (!el) return;
    if (el.innerText !== editorText) {
      el.innerHTML = linkifyHtml(editorText);
    }
  }, [open, editorText, isEditing]);

  const handleEditorInput = (text: string) => {
    const lines = text.split('\n');

    const newTitle = lines[0] ?? '';
    // keep exact spacing/newlines after title
    const newBody = lines.slice(1).join('\n');

    setTitle(newTitle);
    setBody(newBody);
  };

  const handleDotsClick = () => {
    if (dotsButtonRef.current) {
      const rect = dotsButtonRef.current.getBoundingClientRect();
      setMenuAnchor(rect);
    }
    copyDeleteModal.openModal();
  };

  const metaLabel = isLoading
    ? 'Loading…'
    : hasPersisted
        ? `Updated ${formattedUpdatedAt}`
        : `Created ${formattedCreatedAt}`;

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }} modal={false}>
      <Dialog.Portal>
        <NotesSheet
          onEscapeKeyDown={(e: Event) => e.preventDefault()}
          onInteractOutside={(e: Event) => e.preventDefault()}
          aria-describedby={undefined}
        >
          <Dialog.Title
            style={{
              position: 'absolute',
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: 'hidden',
              clip: 'rect(0,0,0,0)',
              whiteSpace: 'nowrap',
              border: 0,
            }}
          >
            Notes
          </Dialog.Title>

          <Header
            disableSidebar
            showBackButton
            onBackNavigate={onClose}
            onNotificationClick={handleNotificationClick}
            onProfileClick={handleProfileClick}
          />

          <NotesBody>
            <NotesMain>
              <NotesInner>
                {/* Loading state */}
                {isLoading && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
                    <UniversalSpinner />
                  </div>
                )}

                {/* Load error */}
                {!isLoading && loadError && (
                  <NoteErrorBanner>{loadError}</NoteErrorBanner>
                )}

                {/* Save error */}
                {!isLoading && saveError && (
                  <NoteErrorBanner>{saveError}</NoteErrorBanner>
                )}

                {/* Note content — hide editor when there's a load error */}
                {!isLoading && !loadError && (
                  <>
                    <MetaRow>
                      <NoteDateText>{metaLabel}</NoteDateText>

                      <DotsButton
                        ref={dotsButtonRef}
                        type="button"
                        aria-label="Note options"
                        onClick={handleDotsClick}
                      >
                        <DotsInner>
                          <Dot />
                          <Dot />
                          <Dot />
                        </DotsInner>
                      </DotsButton>
                    </MetaRow>

                    <NoteContent>
                      <NoteEditor
                        ref={setEditorRef}
                        contentEditable
                        suppressContentEditableWarning
                        role="textbox"
                        aria-multiline="true"
                        data-placeholder="Title"
                        onFocus={() => setIsEditing(true)}
                        onBlur={() => setIsEditing(false)}
                        onClick={(e) => {
                          // If the user clicks a link, open it instead of editing
                          const target = e.target as HTMLElement;
                          if (target.tagName === 'A') {
                            e.preventDefault();
                            const href = (target as HTMLAnchorElement).href;
                            if (href) window.open(href, '_blank', 'noopener,noreferrer');
                          }
                        }}
                        onInput={(e) => handleEditorInput(e.currentTarget.innerText || '')}
                      />
                    </NoteContent>
                  </>
                )}
              </NotesInner>
            </NotesMain>
          </NotesBody>

          <NotesCopyDeleteModal
            open={copyDeleteModal.open}
            onClose={copyDeleteModal.closeModal}
            onCopy={copyDeleteModal.handleCopy}
            onDelete={copyDeleteModal.handleDelete}
            anchorRect={menuAnchor}
            copyLabel={copyDeleteModal.copyLabel}
          />

          <ConfirmDeleteModal
            open={confirmDeleteModal.open}
            onCancel={confirmDeleteModal.handleCancel}
            onConfirm={confirmDeleteModal.handleConfirm}
            isDeleting={confirmDeleteModal.isDeleting}
            deleteError={confirmDeleteModal.deleteError}
          />
        </NotesSheet>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default Notes;
